import type { ReactNode } from "react";

import dayjs from "dayjs";
import { styled } from "styled-components";

import { Download } from "@similarweb/icons";
import { shortMonthDayAndYear, shortMonthAndYearWithDelimiter } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { FlexColumn, PixelPlaceholderLoader } from "@similarweb/ui-components";

import { useMediaPreviewContext } from "app/components/media-preview/MediaPreviewContext";
import { translate } from "app/filters/i18nFilter";
import { numberFilter } from "app/filters/numberFilter";
import type { AdIntelligenceController } from "app/pages/digital-marketing/ad-intelligence/common/shared/types/types";
import { NA_VALUE, isDailyGranularityLocked } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { allTrackers } from "app/services/track/track";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import type { AdsGalleryData } from "../hooks/useAdsGallery";
import { creativeTypeToInfoMap } from "../hooks/useAdsGallery";
import { useAdSnapshot } from "../hooks/useAdSnapshot";
import { UrlAnchor } from "../UrlAnchor";

import { DetailsLabel, ModalSectionTitle, DetailsValue } from "./StyledComponents";

const DetailRows = styled(FlexColumn)`
    row-gap: 16px;
    margin-top: 24px;
`;

const DetailRow = ({ label, value }: { label: string; value: ReactNode }) => {
    return (
        <FlexRow data-automation="detail-row">
            <DetailsLabel>{label}</DetailsLabel>
            <DetailsValue>{value}</DetailsValue>
        </FlexRow>
    );
};

const SnapshotIconLink = styled.a`
    display: flex;
    align-items: center;
    cursor: pointer;
`;

const FULL_MONTH_AND_YEAR = "MMMM, YYYY";

type ModalDetailsProps = Pick<
    AdsGalleryData,
    "creativeType" | "dimensions" | "activeDays" | "firstSeen" | "lastSeen" | "landingPagesLinks" | "creativeId"
> & {
    controller?: AdIntelligenceController;
    isOverviewContext?: boolean;
};

export const ModalDetails = ({
    creativeType,
    dimensions,
    activeDays,
    firstSeen,
    lastSeen,
    landingPagesLinks,
    controller = "advertiser",
    creativeId,
    isOverviewContext = false,
}: ModalDetailsProps) => {
    const { state: mediaState } = useMediaPreviewContext();
    const extractedVideoDuration = creativeType === "video" ? mediaState.duration : null;
    const durationHooked = isDailyGranularityLocked();
    const dateFormat = isOverviewContext
        ? FULL_MONTH_AND_YEAR
        : durationHooked
          ? shortMonthAndYearWithDelimiter
          : shortMonthDayAndYear;
    const firstSeenFormatted = dayjs(firstSeen).format(dateFormat);
    const lastSeenFormatted = dayjs(lastSeen).format(dateFormat);
    const dimensionsFormatted = dimensions?.split("x").join(" X ") + " px";
    const isVideo = creativeType === "video";
    const { url: adSnapshotUrl, isLoading: isAdSnapshotLoading } = useAdSnapshot(controller, creativeId);

    const formatVideoDuration = (): ReactNode => {
        if (mediaState.status === "error" || extractedVideoDuration === 0) {
            return NA_VALUE;
        }
        if (extractedVideoDuration === null) {
            return (
                <PixelPlaceholderLoader
                    width="40px"
                    height="16px"
                />
            );
        }
        const minutes = Math.floor(extractedVideoDuration / 60);
        const seconds = Math.floor(extractedVideoDuration % 60);
        return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    };

    const handleSnapshotClick = () => {
        allTrackers.dispatchTrackEvent("External Link", "click", "Ads/Snapshot/Modal");
    };

    return (
        <div>
            <ModalSectionTitle>ad.intelligence.advertiser.ad.modal.details.title</ModalSectionTitle>
            <DetailRows>
                <DetailRow
                    label={translate("ad.intelligence.advertiser.ad.modal.details.type.label")}
                    value={translate(creativeTypeToInfoMap[creativeType]?.name)}
                />
                {isVideo ? (
                    <DetailRow
                        label={translate("ad.intelligence.advertiser.ad.modal.details.video.duration.label")}
                        value={formatVideoDuration()}
                    />
                ) : null}
                <DetailRow
                    label={translate("ad.intelligence.advertiser.ad.modal.details.dimensions.label")}
                    value={dimensionsFormatted}
                />
                {!isOverviewContext && !durationHooked && (
                    <DetailRow
                        label={translate("ad.intelligence.advertiser.ad.modal.details.duration.label")}
                        value={`${numberFilter()(activeDays)} ${translate(
                            activeDays > 1
                                ? "ad.intelligence.advertiser.ad.modal.details.duration.days"
                                : "ad.intelligence.advertiser.ad.modal.details.duration.day",
                        )}`}
                    />
                )}
                <DetailRow
                    label={translate("ad.intelligence.advertiser.ad.modal.details.seen.label")}
                    value={`${firstSeenFormatted} - ${lastSeenFormatted}`}
                />
                <DetailRow
                    label={translate(
                        landingPagesLinks?.length > 1
                            ? "ad.intelligence.advertiser.ad.modal.details.landing.pages.label"
                            : "ad.intelligence.advertiser.ad.modal.details.landing.page.label",
                    )}
                    value={landingPagesLinks ? <UrlAnchor landingPagesLinks={landingPagesLinks} /> : <>{NA_VALUE}</>}
                />
                {controller === "publisher" ? (
                    <DetailRow
                        label={translate("ad.intelligence.advertiser.ad.card.top.publishers.screenshot.column.title")}
                        value={
                            isAdSnapshotLoading ? (
                                <PixelPlaceholderLoader
                                    width="100px"
                                    height="16px"
                                />
                            ) : adSnapshotUrl ? (
                                <SnapshotIconLink
                                    onClick={handleSnapshotClick}
                                    target={"_blank"}
                                    rel={"noreferrer"}
                                    href={adSnapshotUrl}
                                >
                                    <Download
                                        size="xs"
                                        color={colorsPalettes.blue[400]}
                                    />
                                </SnapshotIconLink>
                            ) : (
                                <>{NA_VALUE}</>
                            )
                        }
                    />
                ) : null}
            </DetailRows>
        </div>
    );
};
